import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { Subject, debounceTime, distinctUntilChanged, lastValueFrom, of, takeUntil, tap } from 'rxjs';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { FullPageMaskService, SpinnerModule } from 'common';
import { CommonModule } from '@angular/common';
import { SenderIdItem } from '../../../interfaces/sender-id.interface';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { authorisedOpasFilterPredicate } from './authorised-opas-dialog-predicate';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { OPAStatusDesc } from '../../../interfaces/opa-status-desc.interface';
import { AuthorisationStatusDirective } from '../../../directives/authorisation-status-color.directive';
import { AccountApiService } from '../../../services/account-api.service';
import { SenderIdAuthorisationItem } from '../../../interfaces/sender-id-authorisation-item.interface';
import { SaveSenderIdAllOpasStatusItem } from '../../../interfaces/save-sender-id-all-opas-status-item.interface';
import { OPAStatus } from '../../../interfaces/opa-status.interface';
import { TableConstants } from '../../../constants/table.constant';

export interface AuthorisedOpasDialogData {
  senderId: SenderIdItem;
}

export interface AuthorisedOpaFormGroup {
  accountId: FormControl<number>;
  authorisationId: FormControl<number>;
  status: FormControl<boolean>;
}

export interface AuthorisedOpaForm {
  accountId: number;
  authorisationId: number;
  status: boolean;
}

@Component({
  selector: 'si-authorised-opas-dialog',
  standalone: true,
  imports: [MatDialogModule, MatButtonModule, MatTableModule, CommonModule, MatIconModule, ReactiveFormsModule, MatCheckboxModule, MatFormField, MatLabel, MatInputModule, AuthorisationStatusDirective, SpinnerModule, MatSlideToggleModule],
  templateUrl: './authorised-opas-dialog.component.html',
  styleUrls: ['./authorised-opas-dialog.component.scss'],
})
export class AuthorisedOpasDialogComponent implements OnInit {
  public readonly columns = ['opa', 'actions'];

  private _destroy$ = new Subject<void>();

  private readonly maskService = inject(FullPageMaskService);
  private readonly dialogRef = inject(MatDialogRef<AuthorisedOpasDialogComponent>);
  private readonly accountApiService = inject(AccountApiService);
  public data = inject<AuthorisedOpasDialogData>(MAT_DIALOG_DATA);
  public readonly fb = inject(FormBuilder);

  public OPAStatusDesc = OPAStatusDesc;
  public TableConstants = TableConstants;

  public isFetching = true;
  public formArray: FormArray<FormGroup<AuthorisedOpaFormGroup>> = this.fb.array<FormGroup<AuthorisedOpaFormGroup>>([]);
  public filter: FormControl = new FormControl('');
  public dataSource = new MatTableDataSource<SenderIdAuthorisationItem>();

  public async ngOnInit(): Promise<void> {
    this.maskService.showLoading();

    try {
      const data = await lastValueFrom<SenderIdAuthorisationItem[]>(this.accountApiService.getSenderIdAllOpas$(this.data.senderId.senderIdID));

      const sortedData = data.sort((a, b) => {
        if (a.status === OPAStatusDesc.Authorised && b.status !== OPAStatusDesc.Authorised) return -1;
        if (a.status === OPAStatusDesc.Authorised && b.status === OPAStatusDesc.Authorised) return 0;

        return 1;
      });

      sortedData.forEach(({ status, accountId, authorisationId }) => this.formArray.push(this.fb.group({
        accountId: new FormControl({ disabled: true, value: accountId }),
        authorisationId: new FormControl({ disabled: true, value: authorisationId }),
        status: new FormControl(status === OPAStatusDesc.Authorised),
      }), { emitEvent: false }));

      this.dataSource = new MatTableDataSource<SenderIdAuthorisationItem>(sortedData);
      this.dataSource.filterPredicate = authorisedOpasFilterPredicate;
    } finally {
      this.maskService.hideLoading();
      this.isFetching = false;
    }

    this.addSubscription();
  }

  private addSubscription() {
    this.filter.valueChanges.pipe(
      takeUntil(this._destroy$),
      debounceTime(500),
      distinctUntilChanged(),
    ).subscribe(value => {
      this.applyFilter(value);
    });
  }

  private applyFilter(value: string) {
    this.dataSource.filter = value.trim().toLowerCase();
  }

  public get disableSaveButton(): boolean {
    return this.formArray.controls.every((control) => control.get('status').pristine);
  }

  public getFormArrayControl(element: SenderIdAuthorisationItem) {
    return this.formArray.controls
      .find(
        (control) => control.get('accountId').value === element.accountId &&
          control.get('authorisationId').value === element.authorisationId)
      .get('status');
  }

  public getFormControlAt(index: number) {
    return this.formArray.at(index).get('status');
  }

  public handleClose() {
    this.dialogRef.close();
  }

  public handleFormSubmit() {
    const parsedData = this.formArray
      .getRawValue()
      .filter((_, index) => this.getFormControlAt(index).dirty)
      .map<SaveSenderIdAllOpasStatusItem>(({ status, ...data }) => ({
        ...data,
        statusId: status ? OPAStatus.Authorised : OPAStatus.NotAuthorised,
      }));

    this.dialogRef.close(parsedData);
  }
}
